import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { isNil } from 'lodash';

import { ApiResponseModel } from '@nocowanie/gql-core';
import { BaseStateModel, LoadingState } from '@nocowanie/core';

import { ReviewSortEnum, SliceNameEnum } from '@app/enums';
import { environment } from '@app/environments/environment';

import { apiTypes, RequestAdditionalParamsModel } from '../../models';
import { ApiAccommodationService } from '../../services';

export interface AccommodationRatingsStateModel extends BaseStateModel {
    data: apiTypes.Maybe<apiTypes.RatingPageConnection>;
    isInitialDataLoaded: boolean;
    pageToLoad: number;
    sortBy: ReviewSortEnum;
    query: string;
}
const initialState: AccommodationRatingsStateModel = {
    isInitialDataLoaded: false,
    loading: LoadingState.IDLE,
    data: null,
    pageToLoad: 1,
    sortBy: ReviewSortEnum.MOST_HELPFUL,
    query: '',
};

export const getRatingsFromAPIWithEs = async (
    args: Omit<apiTypes.QueryRatingsArgs, 'page'> & RequestAdditionalParamsModel,
    page = 1,
    limit = environment.REVIEWS_PAGINATION_LIMIT,
    sortBy: ReviewSortEnum = ReviewSortEnum.MOST_HELPFUL,
    query = '',
) => {
    const from = (page - 1) * limit;
    const result = await ApiAccommodationService.instance.loadAccommodationRatings(
        args.accommodationId,
        from,
        limit,
        sortBy,
        query,
    );

    const ratings =
        result?.hits?.map(
            ({ _source, highlight }: { _source: any; highlight?: Record<string, any> }) => ({
                ..._source,
                highlight,
                average: Number(_source.average ?? 0),
            }),
        ) || [];

    const ratingCount = result?.total?.value || 0;

    return {
        data: {
            collection: !isNil(ratings) ? ratings : undefined,
            paginationInfo: {
                totalCount: ratingCount,
                itemsPerPage: limit,
                lastPage: Math.ceil(ratingCount / limit),
            },
        },
    };
};

const fetchRatingsThunk = createAsyncThunk<
    ApiResponseModel<apiTypes.RatingPageConnection>,
    Omit<apiTypes.QueryRatingsArgs, 'page'>
>(
    `${SliceNameEnum.AccommodationRatings}/fetch-accommodation-ratings`,
    async (args, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();
            const accommodationRatingsState = state[SliceNameEnum.AccommodationRatings];
            const { pageToLoad, sortBy, query } = accommodationRatingsState;

            return await getRatingsFromAPIWithEs(
                args,
                pageToLoad,
                environment.REVIEWS_PAGINATION_LIMIT,
                sortBy,
                query,
            );
        } catch (err) {
            return rejectWithValue([err].flat());
        }
    },
);

const slice = createSlice({
    name: SliceNameEnum.AccommodationRatings,
    initialState,
    reducers: {
        setSortBy: (state, action: PayloadAction<ReviewSortEnum>) => {
            const { payload } = action;

            if (state.sortBy === payload) {
                return;
            }

            state.sortBy = payload;
            state.data = null;
            state.isInitialDataLoaded = false;
            state.loading = LoadingState.IDLE;
            state.pageToLoad = 1;
        },
        setQuery: (state, action: PayloadAction<string>) => {
            const trimmedQuery = action.payload.trim();

            if (state.query === trimmedQuery) {
                return;
            }

            state.query = trimmedQuery;
            state.data = null;
            state.isInitialDataLoaded = false;
            state.loading = LoadingState.IDLE;
            state.pageToLoad = 1;
        },
        resetData: state => {
            return {
                ...initialState,
            };
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchRatingsThunk.pending, state => {
            state.loading = LoadingState.PENDING;
        });
        builder.addCase(fetchRatingsThunk.fulfilled, (state, { payload }) => {
            if (!payload) {
                return;
            }

            const { data } = payload;
            state.isInitialDataLoaded = true;
            state.loading = LoadingState.SUCCESS;
            state.errors = null;

            if (!state.data || !state.data.collection) {
                state.data = data;
            } else {
                state.data = {
                    collection: [...state.data.collection, ...(data.collection || [])],
                    paginationInfo: data.paginationInfo,
                };
            }

            state.pageToLoad = state.pageToLoad + 1;
        });

        builder.addCase(fetchRatingsThunk.rejected, (state, action) => {
            state.errors = [action.error].flat();
            state.loading = LoadingState.ERROR;
            state.isInitialDataLoaded = true;
        });
    },
});

const { actions: sliceActions } = slice;

const actions = {
    ...sliceActions,
    fetchRatingsThunk,
};

const selectSelfState = (state: {
    [SliceNameEnum.AccommodationRatings]: AccommodationRatingsStateModel;
}) => {
    return state[slice.name] ?? initialState;
};

const selectors = {
    selectLoading: createSelector(selectSelfState, state => state.loading),
    selectRatings: createSelector(selectSelfState, state => state.data?.collection || []),
    selectTotalCount: createSelector(
        selectSelfState,
        state => state.data?.paginationInfo.totalCount || 0,
    ),
    selectIsInitialDataLoaded: createSelector(selectSelfState, state => state.isInitialDataLoaded),
    selectHasPagesToLoad: createSelector(selectSelfState, state => {
        const lastPage = state.data?.paginationInfo?.lastPage;
        return !isNil(lastPage) && state.pageToLoad <= lastPage;
    }),
    selectQuery: createSelector(selectSelfState, state => state.query),
    selectSortBy: createSelector(selectSelfState, state => state.sortBy),
};

export { slice, actions, selectors };
